import { useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  collectPipelineWarnings,
  PipelineConfig,
  pipelineFileContents,
  renderChainPreview,
} from '@internal/backstage-plugin-sql-pipeline-common';

/**
 * 저장 전에 **실제로 커밋될 파일**을 보여준다.
 *
 * 비개발자에게 "내가 뭘 만드는지" 를 보여주는 가장 강력한 장치다. 지금까지는
 * 폼을 채우고 나면 PR 링크만 받았고, 거기서 처음으로 파일을 보게 됐다 —
 * 그 시점에는 이미 PR 이 올라가 있다.
 *
 * `pipelineFileContents` 는 백엔드 커밋 경로가 쓰는 **같은 함수**다
 * (`-common` 에 있다). `/preview` 라우트를 두는 쪽은 왕복이 생기고 두 구현이
 * 갈라질 여지가 남는다 — 미리보기가 거짓말을 하면 안 하는 것보다 나쁘다.
 */

const useStyles = makeStyles(theme => ({
  code: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 12.5,
    lineHeight: 1.6,
    whiteSpace: 'pre',
    overflowX: 'auto',
    color: theme.palette.text.primary,
  },
  path: {
    fontFamily: 'monospace',
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  notice: {
    padding: theme.spacing(1.25, 1.75),
    marginBottom: theme.spacing(2),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  chain: {
    padding: theme.spacing(1.25, 1.75),
    marginBottom: theme.spacing(2),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    fontFamily: 'monospace',
    fontSize: 12.5,
    whiteSpace: 'pre-wrap',
  },
  tabLabel: { fontFamily: 'monospace', textTransform: 'none' },
}));

export const PipelinePreview = (props: { config: PipelineConfig }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const files = pipelineFileContents(props.config);
  const warnings = collectPipelineWarnings(props.config);
  const active = files[Math.min(tab, files.length - 1)];

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" paragraph>
        아래 파일들이 <strong>{props.config.dag_folder}</strong> 아래에 그대로
        만들어집니다. 새 DAG 은 꺼진 상태로 올라오므로 머지 후 Airflow 에서 토글을
        켜야 합니다.
      </Typography>

      <Box className={classes.chain}>
        {renderChainPreview(props.config.groups, props.config.group_execution)}
      </Box>

      {warnings.length > 0 && (
        <Box className={classes.notice}>
          <Typography
            variant="caption"
            component="div"
            style={{ fontWeight: 700, marginBottom: 4 }}
          >
            확인해주세요 (저장은 됩니다) — 이 내용은 PR 본문에도 적힙니다
          </Typography>
          {warnings.map(w => (
            <Typography
              key={w.message}
              variant="body2"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {w.message}
            </Typography>
          ))}
        </Box>
      )}

      <Tabs
        value={Math.min(tab, files.length - 1)}
        onChange={(_e, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        indicatorColor="primary"
        textColor="primary"
      >
        {files.map(file => (
          <Tab
            key={file.path}
            className={classes.tabLabel}
            label={file.path.split('/').slice(-2).join('/')}
          />
        ))}
      </Tabs>

      <Box mt={2}>
        <Typography variant="caption" color="textSecondary" className={classes.path}>
          {active.path}
        </Typography>
        <pre className={classes.code}>{active.content}</pre>
      </Box>
    </Box>
  );
};
